import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import type { GenerateItineraryPayload, ItineraryPlan } from '@/types/itinerary';

/**
 * POST /itinerary/generate — build an optimized, slot-aware visit schedule from
 * an ordered list of attraction ids. Gated by `requireJWT` only, so the cashier
 * token works; the shared `http` client injects the Bearer token.
 *
 * The backend is the single scheduling authority — it returns every requested
 * attraction, each either scheduled (carrying its authoritative `slotId` +
 * slot window) or unscheduled (with an `unscheduledReason`). The caller books
 * against those ids directly.
 *
 * Errors surface as a typed {@link import('@/types/api').ApiError} via
 * `toApiError`, preserving the numeric `code` (401/422/429/400) and the
 * backend `errorCode` string (e.g. VALIDATION_ERROR, RATE_LIMITED).
 */
export async function generateItinerary(
  payload: GenerateItineraryPayload,
): Promise<ItineraryPlan> {
  try {
    const res = await http.post<ApiEnvelope<ItineraryPlan>>('/itinerary/generate', payload);
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
